import ACLAuthority from '@app/model/profile/ACLAuthority';
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import Utilities from '@util/Utilities';
import { useCompanyHotels } from '@app/site/secured/rateshopper/rates/provider/useCompanyHotels';
import SaveCompanyProfileModal from '@app/site/secured/general-settings/fragment/property-access/modal/SaveCompanyProfileModal';
import Avatar from '@app/component/avatar/Avatar';
import Tag from '@app/component/tag/Tag';
import RegistrationState from '@app/model/authentication/RegistrationState';
import Authority from '@app/model/authentication/Authority';
import FormButton from '@app/component/form/button/FormButton';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import CompanyProfileInvitationService
	from '@app/model/company_profile_invitation/service/CompanyProfileInvitationService';
import { CompanyProfileService } from '@app/model/company_profile/service/CompanyProfileService';
import { CompanyProfileQueryKeys } from '@app/model/company_profile/service/CompanyProfileQueryKeys';
import { CompanyProfileOutput } from '@app/model/company_profile/output/CompanyProfileOutput';
import { useTranslation } from '@app/component/translation/Translation';
import useSecurity from '@util/SecurityHook';
import useDialogue from '@app/component/overlays/dialog/DialogueRef';
import Dialogue from '@app/component/overlays/dialog/Dialogue';
import { Tooltip } from 'react-tooltip';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';


export const CompanyProfilesTableFragment = React.memo(() => {
	const { companyHotels } = useCompanyHotels();
	const toaster = useToaster();
	const { t } = useTranslation();
	const security = useSecurity();
	const disableDialog = useDialogue<number>();
	const [searchQuery, setSearchQuery] = React.useState('');

	const [selectedCompanyProfile, setSelectedCompanyProfile] = React.useState<CompanyProfileOutput>();

	const selectCompanyProfile = (companyProfile: CompanyProfileOutput) => {
		setSelectedCompanyProfile(companyProfile);
	};

	const unselectCompanyProfile = () => {
		setSelectedCompanyProfile(undefined);
	};

	const queryClient = useQueryClient();

	const resendMutation = useMutation({
		mutationFn: CompanyProfileInvitationService.resendCompanyProfileInvitation,
		onSuccess: () => {
			toaster.success('Property Access', 'Invitation email has been sent successfully!');
		}, onError: (e: string) => {
			toaster.error('Property Access', t(e));
		},
	});

	const disableProfileMutation = useMutation({
		mutationFn: CompanyProfileService.disableCompanyProfile,
		onSuccess: (data) => {
			toaster.success('Property Access', 'Profile has been disabled successfully!');
			queryClient.setQueryData(CompanyProfileQueryKeys.list(), data)
		}, onError: (e: string) => {
			toaster.error('Property Access', t(e));
		},
	});

	const disableProfile = () => {
		const profileId = disableDialog.getValue();
		if (profileId !== undefined && profileId !== null) {
			disableProfileMutation.mutate(profileId);
		}
	};

	const { data: outputs, isLoading, isSuccess } = useQuery({
		queryFn: CompanyProfileService.findCompanyProfiles,
		queryKey: CompanyProfileQueryKeys.list(),
	});

	const filteredOutputs = React.useMemo(() => {
		const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase();

		return (outputs ?? []).filter(output => {
			if (!output.profile.isEnabled) return false;
			if (normalizedSearchQuery === '') return true;

			return [
				output.profile.name,
				output.profile.email,
				...output.profile.accessControl.map(accessControl => accessControl.hotelName),
			].some(value => value.toLocaleLowerCase().includes(normalizedSearchQuery));
		});
	}, [outputs, searchQuery]);

	if (isLoading) return <TablePlaceholder />;

	if (isSuccess) {
		return <>
			<div className="mb-4 grid w-full max-w-md grid-cols-1">
				<input
					type="search"
					name="company-profile-search"
					aria-label="Search users"
					autoComplete="off"
					className="col-start-1 row-start-1 block input w-full rounded-md bg-white py-2 pl-10 pr-3 text-sm"
					placeholder="Search by name, email, or property"
					value={searchQuery}
					onChange={event => setSearchQuery(event.target.value)} />
				<MagnifyingGlassIcon
					className="pointer-events-none col-start-1 row-start-1 ml-3 h-5 w-5 self-center text-gray-400"
					aria-hidden="true" />
			</div>
			<table className="min-w-full divide-y divide-gray-300">
				<thead>
				<tr>
					<th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
						Name
					</th>
					<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
						Status
					</th>
					<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
						Properties
					</th>
					<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
						Role
					</th>
					<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Pricing rights</th>
					<th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
						<span className="sr-only">Edit</span>
					</th>
				</tr>
				</thead>
				<tbody className="divide-y divide-gray-200 bg-white">
				{filteredOutputs.length === 0 && <tr>
					<td colSpan={6} className="px-3 py-10 text-center text-sm text-gray-500">
						No users match your search.
					</td>
				</tr>}
				{filteredOutputs.map(output => <tr key={output.profile.id}>
					<td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm sm:pl-0">
						<div className="flex items-center">
							<div className="size-11 shrink-0">
								<Avatar principal={output.profile} />
							</div>
							<div className={`ml-4 ${!output.profile.isEnabled ? 'opacity-50' : ''}`}>
								<div className="font-medium text-gray-900">{output.profile.name}</div>
								<div className="mt-1 text-gray-500">{output.profile.email}</div>
							</div>
						</div>
					</td>
					<td className="whitespace-nowrap px-3 py-5 text-sm ">
						<Tag
							state={output.profile.registrationState === RegistrationState.COMPLETED ? 'success' : 'warning'}>{Utilities.snakeCaseToWords(output.profile.registrationState)}</Tag>
						{output.profile.registrationState === RegistrationState.PENDING_INVITATION_ACCEPTANCE &&
							<div className="flex text-center">
								<a className="font-medium text-blue-600 hover:text-blue-500 cursor-pointer mt-1 ml-1 text-xs"
									onClick={() => resendMutation.mutate(output.profile.id)}
								>
									Resend Invitation
								</a>
							</div>}
					</td>
					<td className="whitespace-nowrap px-2 py-3 text-sm text-gray-500">
						<div data-tooltip-id={output.profile.id + '_tooltip'} className="flex flex-col gap-y-1">
							<span>{Utilities.trim(output.profile.accessControl[0].hotelName, 30)} </span>
							{output.profile.accessControl.length > 1 && <span
								className="italic text-xs text-gray-400">{`(+ ${output.profile.accessControl.length - 1} more)`}</span>}
						</div>
						<Tooltip id={output.profile.id + '_tooltip'} content={output.profile.accessControl.map(accessControl => accessControl.hotelName).join(', ')}/>
					</td>
					<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
						{output.profile.authorities.includes(Authority.ADMIN) && <span>Admin</span>}
						{output.profile.authorities.includes(Authority.USER) && <span>User</span>}
					</td>
					<td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500 capitalize">{(output.profile.pricingAuthority ?? ACLAuthority.NONE).toLowerCase()}</td>
					<td className="whitespace-nowrap px-3 py-5 text-sm">
						{output.profile.id != security.principal.id && <>
							{!output.profile.isEnabled && <FormButton state="text" isLink extraClassName='text-green-500'
																				onClick={() => selectCompanyProfile(output)}
							>
								Enable
							</FormButton>}
							{output.profile.isEnabled && <FormButton state="text" isLink
																			onClick={() => selectCompanyProfile(output)}
							>
								Edit
							</FormButton>}
							{security.principal.id != output.profile.id && output.profile.isEnabled && security.isAdmin() &&  <>
								<span>|</span>
								<FormButton state="text" isLink
														extraClassName="text-red-500"
														onClick={() => disableDialog.open(output.profile.id)}
								>
									Disable
								</FormButton>
							</>}
						</>}
					</td>
				</tr>)}
				</tbody>
			</table>

			{!!selectedCompanyProfile && <SaveCompanyProfileModal companyHotels={companyHotels}
																														autoShow={true}
																														companyProfile={selectedCompanyProfile}
																														onClose={unselectCompanyProfile} />}
			<Dialogue ref={disableDialog.ref} title="Disable Profile" onConfirm={disableProfile}>
				Are you sure you want to disable this profile?
			</Dialogue>

		</>;
	}

	return <Message state="error">Profiles cannot be loaded!</Message>;
});
